import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Calendar, Mail, Phone } from "lucide-react";
import { BOOKING_STATUS, type BookingStatus } from "@avestate/shared";
import { useBookings, usePatchBooking, type Booking } from "@/hooks/useBookings.js";

function titleIt(t: unknown): string {
  if (t && typeof t === "object" && "it" in t) return String((t as { it: unknown }).it ?? "");
  return "";
}
const fmtDateTime = (iso: string) => new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
const fmtDate = (d: string | null) => (d ? new Date(d).toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "short" }) : null);
const DOT: Record<string, string> = { richiesta: "bg-accent", confermata: "bg-success", completata: "bg-ink-mute", annullata: "bg-risk" };

export default function Bookings(): JSX.Element {
  const { data, isLoading } = useBookings();
  const patch = usePatchBooking();
  const items = data ?? [];

  async function setStatus(b: Booking, next: BookingStatus) {
    try {
      await patch.mutateAsync({ id: b.id, status: next });
      toast.success(`Visita → ${next}`);
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  const upcoming = items.filter((b) => b.status === "richiesta" || b.status === "confermata").length;

  return (
    <div className="space-y-[22px]">
      <header>
        <h1 className="font-display text-[27px] font-bold tracking-tight text-ink">Visite</h1>
        <p className="mt-[5px] text-[13.5px] text-ink-soft">Richieste di sopralluogo dagli immobili pubblici.</p>
      </header>

      <div className="grid grid-cols-2 overflow-hidden rounded-[5px] border border-rule bg-surface sm:grid-cols-4">
        <Stat v={String(items.length)} l="Totali" />
        <Stat v={String(items.filter((b) => b.status === "richiesta").length)} l="Da confermare" />
        <Stat v={String(upcoming)} l="In agenda" />
        <Stat v={String(items.filter((b) => b.status === "completata").length)} l="Completate" last />
      </div>

      <div className="ticks relative rounded-[5px] border border-rule bg-surface">
        <table className="w-full border-collapse">
          <thead>
            <tr>
              <Th>Contatto</Th>
              <Th>Immobile</Th>
              <Th>Data preferita</Th>
              <Th>Stato</Th>
              <Th className="text-right">Ricevuta</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((b) => (
              <tr key={b.id} className="border-b border-rule-soft transition-colors last:border-b-0 hover:bg-surface-2">
                <td className="p-[12px_14px]">
                  <div className="text-[13.5px] font-semibold text-ink">{b.name}</div>
                  <div className="flex items-center gap-3 font-mono text-[11px] text-ink-mute">
                    <a href={`mailto:${b.email}`} className="inline-flex items-center gap-1 hover:text-accent"><Mail className="size-3" />{b.email}</a>
                    {b.phone ? <a href={`tel:${b.phone}`} className="inline-flex items-center gap-1 hover:text-accent"><Phone className="size-3" />{b.phone}</a> : null}
                  </div>
                </td>
                <td className="p-[12px_14px] text-[13px] text-ink-soft">
                  {b.listing_id ? (
                    <Link to={`/listings/${b.listing_id}`} className="hover:text-accent">
                      {titleIt(b.listing?.title) || b.listing?.slug || "immobile"}
                    </Link>
                  ) : <span className="text-ink-mute">—</span>}
                </td>
                <td className="whitespace-nowrap p-[12px_14px] text-[13px] text-ink">
                  {fmtDate(b.preferred_date) ? (
                    <span className="inline-flex items-center gap-[7px]"><Calendar className="size-[13px] text-ink-mute" />{fmtDate(b.preferred_date)}{b.preferred_slot ? <span className="text-ink-mute"> · {b.preferred_slot}</span> : null}</span>
                  ) : <span className="text-ink-mute">flessibile</span>}
                </td>
                <td className="p-[12px_14px]">
                  <span className="mb-1 inline-flex h-[24px] items-center gap-[7px] rounded-full border border-rule bg-surface-2 px-[10px] text-[11.5px] text-ink-soft">
                    <span className={`size-[6px] rounded-full ${DOT[b.status]}`} />{b.status}
                  </span>
                  <select
                    value={b.status}
                    onChange={(e) => void setStatus(b, e.target.value as BookingStatus)}
                    aria-label="Cambia stato visita"
                    className="ml-2 h-[24px] rounded-[4px] border border-rule bg-surface-2 px-1 text-[11px] text-ink-soft [&>option]:bg-surface"
                  >
                    {BOOKING_STATUS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="whitespace-nowrap p-[12px_14px] text-right font-mono text-[11.5px] text-ink-mute">{fmtDateTime(b.created_at)}</td>
              </tr>
            ))}
            {!isLoading && items.length === 0 ? (
              <tr><td colSpan={5} className="p-16 text-center text-ink-mute">Nessuna richiesta di visita.</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ v, l, last }: { v: string; l: string; last?: boolean }): JSX.Element {
  return (
    <div className={`p-[16px_18px] ${last ? "" : "border-r border-rule-soft"}`}>
      <div className="font-mono text-[25px] font-semibold tracking-tight text-ink">{v}</div>
      <div className="micro mt-[5px]">{l}</div>
    </div>
  );
}
function Th({ children, className = "" }: { children: React.ReactNode; className?: string }): JSX.Element {
  return <th className={`border-b border-rule p-[11px_14px] text-left font-mono text-[10px] font-medium uppercase tracking-[0.1em] text-ink-mute ${className}`}>{children}</th>;
}
